/**
 * Whether a refusal on the signer's page means the document changed under
 * them while they were on it, so the page should read the invitation again
 * and show the state it is in now, instead of a sentence over a consent or
 * signing card that can no longer finish.
 *
 * envelope_declined: somebody else declined while this person was signing;
 * the page then shows "This document was declined", as a reload did.
 * group_filled: another member of this person's signing group signed first;
 * the page then shows "Already signed". Every other refusal is about what
 * this person did, and is said where they did it.
 */
export function closedMeanwhile(code: string | undefined): boolean {
  return code === 'envelope_declined' || code === 'group_filled'
}

/**
 * What the signer's page does with a refusal of agreeing, signing or
 * declining, beyond saying it.
 *
 * 'reread': the document or the link changed under the person, so the page
 * reads the invitation again and shows the state it is in now. Besides
 * closedMeanwhile, a link the sender withdrew (invalid_link: withdrawing
 * revokes every link, and a revoked link answers as an unknown one) or one
 * that expired while the person was on it. The page then shows "This link
 * doesn't work" with the server's sentence. Until review 6 these two were
 * said, if at all, beside a button that could only be refused again.
 *
 * 'reopen': the signing session ended (session_required: it lapsed after
 * the idle allowance, which only a POST moves, or it was ended elsewhere).
 * The page goes back to the invitation, where the person opens the document
 * again, with the server's sentence above the button.
 *
 * 'final': this person can never sign this envelope, whatever they do
 * (no_signature_box: a box of theirs that no signature of theirs covers, or
 * no signature box at all). The page says so and holds Sign document off;
 * Decline stays. It used to leave Sign document on, and pressing it again
 * failed the same way. The app holds Sign off for it too (61a619f).
 *
 * 'say': a refusal about what this person did, said where they did it.
 */
export function afterRefusal(code: string | undefined): 'reread' | 'reopen' | 'final' | 'say' {
  if (closedMeanwhile(code) || code === 'invalid_link' || code === 'expired_link') return 'reread'
  if (code === 'session_required') return 'reopen'
  if (code === 'no_signature_box') return 'final'
  return 'say'
}
